// SPDX-License-Identifier: AGPL-3.0-or-later

import {TextualAttachmentLanguagePopout} from '@app/features/channel/components/embeds/attachments/TextualAttachmentLanguagePopout';
import styles from '@app/features/channel/components/embeds/attachments/TextualAttachmentPreview.module.css';
import {
	getAttachmentFileName,
	type TextualAttachmentPreviewFooterProps,
} from '@app/features/channel/components/embeds/attachments/TextualAttachmentPreviewUtils';
import {formatFileSize} from '@app/features/messaging/utils/FileUtils';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {Popout} from '@app/features/ui/popover/Popout';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import {msg, plural} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {ArrowsOutIcon, CaretDownIcon, CodeIcon, DotsThreeIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
export function TextualAttachmentPreviewFooter({
	attachment,
	canExpand,
	inferredLanguageCode,
	isExpanded,
	lineCount,
	onMoreOptions,
	onOpenFullscreen,
	onSelectLanguage,
	onToggleExpanded,
	selectedLanguage,
	showExpandButton = true,
}: TextualAttachmentPreviewFooterProps) {
	const {i18n} = useLingui();
	const fileName = getAttachmentFileName(attachment);
	let fileSizeLabel = '';
	if (typeof attachment.size === 'number') {
		fileSizeLabel = formatFileSize(attachment.size);
	}
	const expandButtonLabel = isExpanded
		? plural(
				{count: lineCount},
				{
					one: 'Collapse (# line)',
					other: 'Collapse (# lines)',
				},
			)
		: plural(
				{count: lineCount},
				{
					one: 'Expand (# line)',
					other: 'Expand (# lines)',
				},
			);
	const languageButtonLabel = i18n._(msg`Change language`);
	const fullscreenButtonLabel = i18n._(msg`View whole file`);
	const moreButtonLabel = i18n._(msg`More Options`);
	return (
		<div className={styles.footer}>
			{showExpandButton && canExpand && onToggleExpanded && (
				<Tooltip text={expandButtonLabel}>
					<FocusRing offset={-2}>
						<button
							type="button"
							className={styles.expandButton}
							onClick={onToggleExpanded}
							aria-label={expandButtonLabel}
							aria-expanded={isExpanded}
						>
							<CaretDownIcon
								size={18}
								weight="bold"
								className={clsx(styles.expandIcon, isExpanded && styles.expandIconExpanded)}
							/>
						</button>
					</FocusRing>
				</Tooltip>
			)}
			<div className={styles.fileSection}>
				<div className={styles.fileMeta}>
					<span className={styles.fileName}>{fileName}</span>
					{fileSizeLabel && <span className={styles.fileSize}>{fileSizeLabel}</span>}
				</div>
			</div>
			<div className={styles.footerActions}>
				<Popout
					position="top-end"
					offsetMainAxis={8}
					tooltip={languageButtonLabel}
					tooltipPosition="top"
					render={({onClose}) => (
						<TextualAttachmentLanguagePopout
							defaultSearchQuery={inferredLanguageCode}
							onClose={onClose}
							onSelectLanguage={onSelectLanguage}
							selectedLanguage={selectedLanguage}
						/>
					)}
				>
					<FocusRing offset={-2}>
						<button type="button" className={styles.controlButton} aria-label={languageButtonLabel}>
							<CodeIcon size={18} weight="regular" />
						</button>
					</FocusRing>
				</Popout>
				{onOpenFullscreen && (
					<Tooltip text={fullscreenButtonLabel}>
						<FocusRing offset={-2}>
							<button
								type="button"
								className={styles.controlButton}
								onClick={onOpenFullscreen}
								aria-label={fullscreenButtonLabel}
							>
								<ArrowsOutIcon size={18} weight="regular" />
							</button>
						</FocusRing>
					</Tooltip>
				)}
				<Tooltip text={moreButtonLabel}>
					<FocusRing offset={-2}>
						<button type="button" className={styles.controlButton} onClick={onMoreOptions} aria-label={moreButtonLabel}>
							<DotsThreeIcon size={18} weight="bold" />
						</button>
					</FocusRing>
				</Tooltip>
			</div>
		</div>
	);
}
